import {
  ConcatTransformation,
  DictionaryLookupTransformation,
  LocallyDerivedTransformation,
  LocallyDerivedTransformationOperation,
  MathTransformation,
} from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { I18nLocale, t } from '@ardoq/i18n';

export type Preset<
  T extends LocallyDerivedTransformation = LocallyDerivedTransformation,
> = {
  name: string;
  description: string;
  icon: IconName;
  transformation: T;
};

export const mathPreset: Preset<MathTransformation> = {
  name: 'Math expression',
  description: 'Mathematical expression using existing fields',
  icon: IconName.LANGUAGE,
  transformation: {
    operation: LocallyDerivedTransformationOperation.MATH,
    config: {
      additionalFields: [],
      expression: '',
    },
  },
};

export const concatPreset: Preset<ConcatTransformation> = {
  name: 'Concatenate fields',
  description: 'Concatenate multiple fields',
  icon: IconName.LIST,
  transformation: {
    operation: LocallyDerivedTransformationOperation.CONCAT,
    config: {
      additionalFields: [],
      joinStr: ', ',
    },
  },
};

export const dictionaryLookupPreset: Preset<DictionaryLookupTransformation> = {
  name: 'Dictionary lookup',
  description: 'Lookup a value in a dictionary',
  icon: IconName.MESSAGE_FORMAT,
  transformation: {
    operation: LocallyDerivedTransformationOperation.DICTIONARY_LOOKUP,
    config: {
      additionalFields: [],
      dictionary: [],
    },
  },
};

export const transformationPresets = [
  dictionaryLookupPreset,
  mathPreset,
  concatPreset,
];

const localizePreset = <T extends LocallyDerivedTransformation>(
  preset: Preset<T>,
  i18nLocale: I18nLocale,
  nameKey: string,
  descriptionKey: string
): Preset<T> => ({
  ...preset,
  name: t(i18nLocale, nameKey, preset.name),
  description: t(i18nLocale, descriptionKey, preset.description),
});

export const localizeTransformationPreset = (
  preset: Preset,
  i18nLocale: I18nLocale
): Preset => {
  switch (preset.transformation.operation) {
    case LocallyDerivedTransformationOperation.MATH:
      return localizePreset(
        preset,
        i18nLocale,
        'scopeData:calculatedFields.math.name',
        'scopeData:calculatedFields.math.description'
      );
    case LocallyDerivedTransformationOperation.CONCAT:
      return localizePreset(
        preset,
        i18nLocale,
        'scopeData:calculatedFields.concat.name',
        'scopeData:calculatedFields.concat.description'
      );
    case LocallyDerivedTransformationOperation.DICTIONARY_LOOKUP:
      return localizePreset(
        preset,
        i18nLocale,
        'scopeData:calculatedFields.dictionaryLookup.name',
        'scopeData:calculatedFields.dictionaryLookup.description'
      );
  }
};
